import {
  IconButton,
  TextInput,
  ReloadIcon as reloadIcon,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
} from '@/shared/ui';
import SearchAndFilterDeviceEvent from './searchAndFilterDeviceEvent';
import useDevicePaginationContainer from './useDevicePaginationContainer';

export type DevicePaginationContainerProps = {
  isDisabled?: boolean;

  keyRecoil: any;
  studyData?: any;
};

/**
 * Toolbar above the device event grid combining search and filter controls with page navigation. useDevicePaginationContainer supplies the page string, max page count, error flag and the previous, next, reload, change, key-down and blur handlers; the arrow buttons swap between grey and coloured icons as they become disabled, and the page input auto-sizes to its content.
 *
 * @param props - Container props carrying the study data whose start, stop and timezone bound the filter controls.
 * @returns The rendered pagination toolbar with its filter panel, reload button, page input and arrows.
 */
const DevicePaginationContainer = ({
  keyRecoil,
  isDisabled,
  studyData,
}: DevicePaginationContainerProps) => {
  const {
    isLoadingStrip,
    isDisabledNext,
    isDisabledPrev,
    maxPageNumber,
    pageString,
    inputSize,
    isError,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
    onClickReloadButton,
  } = useDevicePaginationContainer({ keyRecoil, isDisabled });

  return (
    <div className="pagination-container">
      <SearchAndFilterDeviceEvent
        keyRecoil={keyRecoil}
        start={studyData?.start}
        stop={studyData?.stop}
        timezone={studyData.timezone}
      />
      <IconButton
        id="device-event-reload-btn"
        isOutline
        iconComponent={<img src={reloadIcon} alt="Noise icon" />}
        onClick={onClickReloadButton}
        className="reset-btn"
        disabled={isLoadingStrip}
      />
      <div className="divider" />
      <IconButton
        id="device-event-previous-page-btn"
        isFilled
        disabled={isDisabledPrev || isDisabled}
        iconComponent={
          <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
        }
        onClick={onClickPrevious}
      />
      <TextInput
        id="device-event-page-input"
        ariaLabel="Page number"
        suffix={`/ ${maxPageNumber}`}
        name="pageString"
        value={pageString}
        onChange={onChangePageString}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        isNumberFormat
        shouldChangeSize
        className="page-input"
        size={inputSize}
        isError={isError}
        disabled={isDisabled}
        decimalSeparator={false}
      />
      <IconButton
        id="device-event-next-page-btn"
        isFilled
        disabled={isDisabledNext || isDisabled}
        iconComponent={<img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />}
        onClick={onClickNext}
      />
    </div>
  );
};

export default DevicePaginationContainer;
